import { useState, useEffect } from 'react';
import {
  Modal, Table, Select, Text, Loader, Center, Alert, Stack, Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { api } from '../../apiClient';

const STATUS_OPTIONS = [
  { value: '', label: '未設定' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'learning', label: 'Learning' },
  { value: 'certified', label: 'Certified' },
  { value: 'retired', label: 'Retired' },
];

const VENDOR_LABEL = { aws: 'AWS', microsoft: 'Microsoft' };

function UserCertificationStatus({ opened, onClose, user }) {
  const [certStatuses, setCertStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    if (!opened || !user) return;
    setLoading(true);
    setError(null);
    api.get('/admin/user-certification-statuses', { userId: user.id })
      .then((data) => setCertStatuses(data ?? []))
      .catch(() => setError('データの取得に失敗しました。'))
      .finally(() => setLoading(false));
  }, [opened, user]);

  const handleStatusChange = async (certId, newStatus) => {
    setSaving((prev) => ({ ...prev, [certId]: true }));
    setError(null);
    try {
      await api.put('/admin/user-certification-statuses', {
        userId: user.id,
        certificationId: certId,
        status: newStatus || null,
      });
      setCertStatuses((prev) =>
        prev.map((c) => c.id === certId ? { ...c, status: newStatus || null } : c)
      );
    } catch {
      setError('ステータスの更新に失敗しました。');
    } finally {
      setSaving((prev) => ({ ...prev, [certId]: false }));
    }
  };

  // Group by vendor
  const byVendor = certStatuses.reduce((acc, c) => {
    const v = c.vendor ?? 'other';
    if (!acc[v]) acc[v] = [];
    acc[v].push(c);
    return acc;
  }, {});

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`資格ステータス管理: ${user?.email ?? ''}`}
      size="lg"
    >
      {loading ? (
        <Center h={150}><Loader size="sm" /></Center>
      ) : (
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" size="sm">
              {error}
            </Alert>
          )}
          {Object.entries(byVendor).map(([vendor, certs]) => (
            <div key={vendor}>
              <Title order={5} mb="xs">{VENDOR_LABEL[vendor] ?? vendor}</Title>
              <Table withTableBorder striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>資格名</Table.Th>
                    <Table.Th>コード</Table.Th>
                    <Table.Th>ステータス</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {certs.map((c) => (
                    <Table.Tr key={c.id}>
                      <Table.Td>
                        <Text size="sm">{c.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace" c="dimmed">{c.code}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          data={STATUS_OPTIONS}
                          value={c.status ?? ''}
                          onChange={(val) => handleStatusChange(c.id, val)}
                          disabled={saving[c.id]}
                          styles={{ input: { minWidth: 110 } }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          ))}
        </Stack>
      )}
    </Modal>
  );
}

export default UserCertificationStatus;
